// Soft — a switch, a stepper, a slider and a value plate. The atoms in
// styles/soft.module.css had a field, a track and buttons but none of these;
// the Settings modal needed them first (2026-10-13) and every other surface
// takes them from here. They hold no state: the surface owns the value, and
// what a click or a key means for it.
//
//   <SoftSwitch>   on / off (role=switch)
//   <SoftStepper>  a whole number with − and + (the buttons stop at the ends)
//   <SoftSlider>   a native range input, so the keyboard and the reader work
//   <SoftValue>    the number beside a slider, typed; its unit after it

import { MinusIcon, PlusIcon } from '@phosphor-icons/react'
import type { CSSProperties } from 'react'
import { Described } from './Described'
import styles from './softControls.module.css'

const join = (...names: (string | undefined)[]) => names.filter(Boolean).join(' ')

export function SoftSwitch({
  checked,
  onChange,
  label,
  disabled = false,
  title,
  className,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  /** The accessible name: what is switched. */
  label: string
  disabled?: boolean
  title?: string
  className?: string
}) {
  return (
    <Described
      as="button"
      type="button"
      className={join(styles.switch, className)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      description={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  )
}

export function SoftStepper({
  value,
  text,
  min,
  max,
  label,
  onText,
  onBlur,
  onStep,
  disabled = false,
}: {
  /** The committed number: what − and + step from. */
  value: number
  /** What the field shows — the surface's own text while it is typed. */
  text: string
  min: number
  max: number
  /** The accessible name of the field. */
  label: string
  onText: (raw: string) => void
  onBlur?: () => void
  onStep: (next: number) => void
  disabled?: boolean
}) {
  return (
    <div className={styles.stepper} data-disabled={disabled || undefined}>
      <button
        type="button"
        className={styles.stepBtn}
        onClick={() => onStep(value - 1)}
        disabled={disabled || value <= min}
        aria-label="Decrease"
      >
        <MinusIcon size={12} weight="bold" aria-hidden="true" />
      </button>
      <input
        className={styles.stepInput}
        inputMode="numeric"
        value={text}
        onChange={(e) => onText(e.target.value)}
        onBlur={onBlur}
        aria-label={label}
        disabled={disabled}
      />
      <button
        type="button"
        className={styles.stepBtn}
        onClick={() => onStep(value + 1)}
        disabled={disabled || value >= max}
        aria-label="Increase"
      >
        <PlusIcon size={12} weight="bold" aria-hidden="true" />
      </button>
    </div>
  )
}

export function SoftSlider({
  value,
  min,
  max,
  step = 1,
  label,
  valueText,
  onChange,
  disabled = false,
  className,
}: {
  value: number
  min: number
  max: number
  step?: number
  /** The accessible name: what is set. */
  label: string
  /** How a reader says the value ("5 seconds"); the number alone otherwise. */
  valueText?: string
  onChange: (next: number) => void
  disabled?: boolean
  className?: string
}) {
  // how far along the line the thumb stands, 0..1
  const p = max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0
  return (
    <input
      type="range"
      className={join(styles.slider, className)}
      min={min}
      max={max}
      step={step}
      value={value}
      style={{ '--p': p } as CSSProperties}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={label}
      aria-valuetext={valueText}
      disabled={disabled}
    />
  )
}

export function SoftValue({
  text,
  unit,
  label,
  onText,
  onBlur,
  disabled = false,
}: {
  text: string
  unit?: string
  /** The accessible name of the field. */
  label: string
  onText: (raw: string) => void
  onBlur?: () => void
  disabled?: boolean
}) {
  return (
    // the plate is the field's hit area: a click on it lands in the input
    <label className={styles.value} data-disabled={disabled || undefined}>
      <input
        inputMode="numeric"
        value={text}
        onChange={(e) => onText(e.target.value)}
        onBlur={onBlur}
        aria-label={label}
        disabled={disabled}
      />
      {unit && (
        <span className={styles.valueUnit} aria-hidden="true">
          {unit}
        </span>
      )}
    </label>
  )
}
